import { z } from 'zod';

// Sub-schemas
const PrintChangesModeSchema = z.union([
  z.object({ printChanges: z.literal(true), printVoids: z.literal(true) }).strict(),
  z.object({ printChanges: z.literal(true), printVoids: z.literal(false) }).strict(),
  z.object({ printChanges: z.literal(false), printVoids: z.literal(true) }).strict(),
  z.object({ printChanges: z.literal(false), printVoids: z.literal(false) }).strict(),
]);

// Main schema
const PrinterSettingsBaseSchema = z.object({
  diningOptionInclude: z.enum(['always', 'onlyNonDefault']).default('onlyNonDefault').readonly(),
  listOtherStations: z.boolean().default(false).readonly(),

  consolidateItems: z.enum(['consolidate', 'split']).default('consolidate').readonly(),
  itemSorting: z.enum(['seatNumber', 'sortOrder', 'inputOrder']).default('inputOrder').readonly(),
  modifierRouting: z.enum(['modifiersOnly', 'itemWithModifiers']).default('itemWithModifiers').readonly(),

  printGuestDetails: z.boolean().default(false).readonly(),
  printDefaultModifiersBlack: z.boolean().default(false).readonly(),
  printChangesMode: PrintChangesModeSchema.default({ printChanges: true, printVoids: true }).readonly(),

  printItemSizesInline: z.boolean().default(true).readonly(),
  groupByPlate: z.boolean().default(false).readonly(),
  splitTicketsByCourse: z.boolean().default(false).readonly(),
  // Owner decision 5 — see IPrinterSettings.alwaysPrintFireTicket.
  alwaysPrintFireTicket: z.boolean().default(false).readonly(),
  // A SECOND expo chit, marked PAID, when a collected order that already went
  // to the kitchen unpaid is paid — see IPrinterSettings.printPaidExpoTicket.
  printPaidExpoTicket: z.boolean().default(false).readonly(),
  // The store's print-coordinator till (device GUID) for a store with no
  // gateway — see IPrinterSettings.printCoordinatorDeviceGuid. Null = any till.
  printCoordinatorDeviceGuid: z.string().min(1).nullable().default(null).readonly(),

  // The ONE clamp set, write side (read side: the constant block in
  // packages/common/src/printing/ticketAppearance.ts, Task G6). Both bounds are
  // what the modal offers (G12) and what the Dart mirror repeats (G13).
  headerSpacing: z.number().int().min(0).max(30).default(12).readonly(),
  footerSpacing: z.number().int().min(0).max(20).default(0).readonly(),
  ticketFooterSections: z.array(z.string()).default([]).readonly(),

  /**
   * Restaurant-scoped launch switches. No key is read today (the
   * `print_document_model` kill switch is retired), but stored documents still
   * carry the map and this object is `.strict()`: WITHOUT this field every
   * existing document that has one would be rejected with "Unrecognized key(s)
   * in object". Keep it until a migration strips the stored maps.
   */
  initiatives: z.record(z.string(), z.boolean()).default({}).readonly(),

}).strict();

// No refinements left: the only one guarded `expediter`, removed 2026-09-26.
const PrinterSettingsSchema = PrinterSettingsBaseSchema;

/** Unwrap `.readonly()` and `.default()` so a field can be parsed as OPTIONAL. */
function withoutDefault(field: z.ZodType): z.ZodType {
  let inner: z.ZodType = field;
  if (inner instanceof z.ZodReadonly) inner = inner.unwrap() as z.ZodType;
  if (inner instanceof z.ZodDefault) inner = inner.unwrap() as z.ZodType;
  return inner;
}

/**
 * `restaurant.settings.printing` — SPARSE. Same field set and bounds as
 * PrinterSettingsSchema, every key optional and with NO defaults: Zod 4
 * applies a `.default()` even inside `.partial()`, so deriving this with
 * `.partial()` would silently materialise every default into any document
 * whose parse output is written back. `.strict()` refuses the four removed
 * fields (printMode, kdsDisplayMode, expediter, ticketScreens) and anything else.
 */
export const LocationPrintingSettingsSchema = z
  .object(
    Object.fromEntries(
      Object.entries(PrinterSettingsBaseSchema.shape).map(([key, field]) => [
        key,
        withoutDefault(field as z.ZodType).optional(),
      ]),
    ) as {
      [K in keyof typeof PrinterSettingsBaseSchema.shape]: z.ZodOptional<z.ZodType>;
    },
  )
  .strict();

export type PrinterSettingsType = z.infer<typeof PrinterSettingsSchema>;

export default PrinterSettingsSchema;
